import type { BillingProfile, SubscriptionInfo } from '@highlight/common/types'
import { formatPlanEndDate } from './billingUtils'

export interface LegacyPlanSunset {
  planLabel: 'Early Access'
  /** ISO instant the plan ends and the account drops to free; null until billing reports it. */
  endsAt: string | null
}

/**
 * The Early Access grant the account is being moved off, or null. It ends on its
 * scheduled downgrade to free, which the sunset tool moves to one date for every
 * grant. The original $20 Pro is not retired here, and the legacy Pro trial ends
 * on its own date. The 1.0 Stripe Billing plans are not in Orb and never appear
 * here.
 */
export function legacyPlanSunset(
  subscription: Pick<SubscriptionInfo, 'plan' | 'isLegacyBilling' | 'currentPeriodEndAt'>,
  billingProfile: Pick<BillingProfile, 'isEarlyAccess'> | null,
): LegacyPlanSunset | null {
  if (subscription.isLegacyBilling || subscription.plan !== 'pro-trial' || !billingProfile?.isEarlyAccess) {
    return null
  }
  // planEndsAt already resolves a trial's period end to its scheduled downgrade.
  return { planLabel: 'Early Access', endsAt: subscription.currentPeriodEndAt ?? null }
}

/**
 * The sunset is announced as a US calendar date, so its copy formats the instant
 * in Pacific time rather than the user's zone, where the same instant can fall on
 * the evening before.
 */
export const LEGACY_SUNSET_TIME_ZONE = 'America/Los_Angeles'

export interface LegacyPlanSunsetCopy {
  /** "Highlight Early Access" */
  planName: string
  /** "October 15, 2026", or null while billing has not reported a date. */
  endsOn: string | null
  /** "Your Highlight Early Access plan ends on October 15, 2026", or "… is being retired". */
  headline: string
  /** "Choose Pro or Max to keep using Highlight after that date." */
  nextStep: string
  /** "Your current plan stays active until then, and switching sooner moves you over right away." */
  staysActive: string
}

/** The wording every retiring-plan surface builds from, so a copy change lands once. */
export function legacyPlanSunsetCopy(sunset: LegacyPlanSunset): LegacyPlanSunsetCopy {
  const planName = `Highlight ${sunset.planLabel}`
  const endsOn = sunset.endsAt ? formatPlanEndDate(sunset.endsAt, LEGACY_SUNSET_TIME_ZONE) : null
  return {
    planName,
    endsOn,
    headline: endsOn ? `Your ${planName} plan ends on ${endsOn}` : `Your ${planName} plan is being retired`,
    nextStep: endsOn
      ? 'Choose Pro or Max to keep using Highlight after that date.'
      : 'Choose Pro or Max to keep using Highlight.',
    staysActive: endsOn
      ? 'Your current plan stays active until then, and switching sooner moves you over right away.'
      : 'Your current plan stays active until you switch.',
  }
}
